import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import { Text, Heading } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useAdminChangeRequestDetails } from "../../hooks/useAdminChangeRequests";
import { useReviewChangeRequest } from "../../hooks/useReviewChangeRequest";
import { ChangeRequestDetailView } from "../../../change-requests/presentation/components/change-request-detail-view";
import { ChangeRequestStatusBadge } from "../../../change-requests/presentation/components/change-request-status-badge";
import { humanizeType } from "../../../change-requests/data/change-request-type";
import { isReviewable } from "../../../change-requests/data/change-request-status";
import {
  ReviewChangeRequestDtoStatusEnum,
  type ReviewChangeRequestDto,
} from "../../../../api";
import { ReviewNoteDialog, type ReviewAction } from "../components/review-note-dialog";

export function AdminChangeRequestReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: request, isLoading, error } = useAdminChangeRequestDetails(id);
  const [pendingAction, setPendingAction] = useState<ReviewAction | null>(null);

  const { submit, isLoading: isSubmitting } = useReviewChangeRequest(id ?? "");

  // Build + send the DTO inline using `note` from the dialog. We deliberately
  // avoid funnelling `note` through React state then calling submit, because
  // the state write doesn't flush before submit reads it (the bug that
  // produced "review note is required" with a non-empty note typed).
  const handleConfirm = async (note: string) => {
    const dto: ReviewChangeRequestDto = {
      status: pendingAction ?? ReviewChangeRequestDtoStatusEnum.Approved,
      reviewNote: note.trim() || undefined,
    };
    await submit(dto);
    setPendingAction(null);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-4 pt-6 pb-6 md:px-8 md:pt-8">
        <ShimmerLoader message="Loading change request…" />
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-4 pt-6 pb-6 md:px-8 md:pt-8">
        <Text variant="body" tone="muted">Failed to load change request. Please try again later.</Text>
      </div>
    );
  }

  const canReview = isReviewable(request.status);

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 pt-6 pb-6 md:px-8 md:pt-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 max-md:flex-wrap">
        <Button
          variant="ghostAccent"
          size="sm"
          leftIcon={<IconArrowLeft size={18} />}
          onClick={() => navigate(AppRoutes.ADMIN_CHANGE_REQUESTS)}
          className="!h-auto !px-0 !py-0"
        >
          <Heading variant="h3" tone="inherit">Change requests</Heading>
        </Button>
        <Text variant="bodySm" tone="muted" className="max-md:hidden">/</Text>
        {/* Phones: title + status drop to their own line under the back link. */}
        <div className="contents max-md:flex max-md:basis-full max-md:flex-wrap max-md:items-center max-md:gap-2">
          <Heading variant="h3" tone="dark">{humanizeType(request.type)}</Heading>
          <ChangeRequestStatusBadge status={request.status} />
        </div>
      </div>

      {/* Submitter */}
      <div className="flex flex-col gap-0.5">
        <Text variant="bodySmBold" tone="dark">{request.userName || "Unknown submitter"}</Text>
        {request.userEmail && <Text variant="bodySm" tone="muted">{request.userEmail}</Text>}
      </div>

      <ChangeRequestDetailView request={request} />

      {canReview && (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
          <div className="px-6 pt-5 pb-4 max-md:px-4">
            <Heading variant="h3" tone="dark">Review actions</Heading>
          </div>
          <div className="border-t border-borderLight px-6 py-5 flex flex-col gap-4 max-md:px-4">
            <Text variant="bodySm" tone="muted">
              Approving applies the proposed changes immediately. Requesting changes loops back to the submitter. Rejecting is terminal — a note is required for both request-changes and reject.
            </Text>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="filledDark"
                size="md"
                disabled={isSubmitting}
                onClick={() => setPendingAction(ReviewChangeRequestDtoStatusEnum.Approved)}
              >
                Approve
              </Button>
              <Button
                variant="secondary"
                size="md"
                disabled={isSubmitting}
                onClick={() => setPendingAction(ReviewChangeRequestDtoStatusEnum.ChangesRequested)}
              >
                Request changes
              </Button>
              <Button
                variant="secondary"
                size="md"
                disabled={isSubmitting}
                onClick={() => setPendingAction(ReviewChangeRequestDtoStatusEnum.Rejected)}
              >
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      <ReviewNoteDialog
        open={pendingAction !== null}
        action={pendingAction}
        isSubmitting={isSubmitting}
        onClose={() => { if (!isSubmitting) setPendingAction(null); }}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
